// The avatar create flow — one experience for every "make an avatar" entry:
// /welcome (first avatar, "Skip for now"), /avatars/new (wardrobe and
// dashboard, "Cancel"), and the in-world / editor modals (AvatarCreateModal).
//
//   0. Free robots (20261027_free_starter_avatar.sql): an account that hasn't
//      claimed one yet can take ONE pre-built avatar for free — the active rows
//      of starter_avatar_templates (cute Meshy robots; the human looks stay paid). It's an
//      instant copy (no build), claimed through /api/avatar/claim-starter
//      (Turnstile + per-IP limit) and equipped straight away. Whatever active
//      templates exist are listed; the section hides when there are none.
//   1. Choose: a starter look (each realistic person and each style is its own
//      card, filterable) with optional "make it look like me", or "Describe it"
//      (the AvatarCreator wizard, inline) — side by side on desktop, stacked on
//      phones.
//   2. Before the paid 3D build: unlock it with a code, Starter, or a Creator
//      package (admins, Council and beta accounts skip this). The server
//      enforces the same thing (consume_ai_quota); this only asks first.
//   3. Build (Tripo, server-side — see utils/buildAvatarFromImage), then done.
//
// `equip`: 'always' wears the new avatar as soon as it's built (first avatar,
// in-world); 'ask' saves it to the wardrobe and lets the person choose.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Box, Camera, Check, ChevronRight, Crown, Gift, Hand, Loader, Mail, RefreshCw, Shirt, Sparkles, Ticket, Wand2, X } from 'lucide-react';
import AvatarCreator from '../AvatarCreator';
import CheckoutModal from '../checkout/CheckoutModal';
import { paymentsEnabled, type ProductKey } from '../../config/payments';
import { AVATAR_STYLES, styleCard, styleDetail, styleImageDataUrl, styleThumbDataUrl, type AvatarStyle, type StarterKind } from '../../data/avatarStyles';
import { claimStarterAvatar, fetchOnboardingStatus, fetchStarterTemplates, redeemAvatarCode, resendConfirmation, type OnboardingStatus, type StarterTemplate } from '../../services/onboarding';
import TurnstileWidget, { turnstileEnabled } from '../TurnstileWidget';
import { supabase } from '../../config/supabase';
import { waitForPaid } from '../../services/checkout';
import { stylizeSelfie } from '../../services/openRouterApi';
import { buildAvatarFromImage, finishAvatarBuild } from '../../utils/buildAvatarFromImage';
import { pendingAvatarBuild, AvatarBuildError } from '../../services/tripoAvatarBuild';
import { setActiveAvatar, type Avatar } from '../../services/avatarService';
import { useWorldStore } from '../../stores/worldStore';
import './AvatarCreateFlow.css';

// Bring-your-own VRM (free: no AI build). Loads the VRM checker on demand.
const VrmUploadDialog = React.lazy(() => import('../vrm/VrmUploadDialog'));

type Phase = 'choose' | 'building' | 'done';
type Filter = 'all' | StarterKind;

export function rememberAvatarLocally(avatar: Avatar) {
  useWorldStore.getState().setAvatarFromDb(avatar);
  try {
    localStorage.setItem('eb-avatar-url', avatar.model_url);
    localStorage.setItem('eb-avatar-animations', JSON.stringify(avatar.animations || {}));
  } catch { /* storage unavailable */ }
}

// ---------- Unlock gate ----------
const UnlockGate: React.FC<{
  status: OnboardingStatus | null;
  onUnlocked: () => void;
  onClose: () => void;
  onBuy: (p: ProductKey | 'council') => void;
}> = ({ status, onUnlocked, onClose, onBuy }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const first = !status?.has_avatar;
  const canRedeem = !status?.redeemed_code;

  const redeem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true); setMsg(null);
    const r = await redeemAvatarCode(code);
    setBusy(false);
    setMsg({ ok: r.ok, text: r.message });
    if (r.ok) window.setTimeout(onUnlocked, 700);
  };

  return (
    <div className="gxw-gate-overlay" onClick={onClose}>
      <div className="gxw-gate" role="dialog" aria-modal="true" aria-label={first ? 'Unlock your first avatar' : 'Unlock another avatar'} onClick={(e) => e.stopPropagation()}>
        <button className="gxw-gate-x" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <span className="gxw-eyebrow"><Gift size={13} /> Almost there</span>
        <h3>{first ? 'Unlock your first avatar' : 'Unlock another avatar'}</h3>
        <p className="gxw-gate-sub">Building a 3D avatar takes real AI compute. Pick how you'd like to unlock it.</p>

        <div className={`gxw-gate-options${canRedeem ? '' : ' is-two'}`}>
          {canRedeem && (
            <form className="gxw-opt" onSubmit={redeem}>
              <span className="gxw-opt-icon"><Ticket size={18} /></span>
              <strong>Have a code?</strong>
              <span className="gxw-opt-note">Your first avatar is free with an invite code.</span>
              <div className="gxw-code">
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ENTER CODE" aria-label="Code" autoCapitalize="characters" spellCheck={false} maxLength={200} />
                <button disabled={busy || !code.trim()}>{busy ? <Loader size={15} className="spin" /> : 'Apply'}</button>
              </div>
              {msg && <span className={`gxw-code-msg ${msg.ok ? 'is-ok' : ''}`} role="status">{msg.ok && <Check size={13} />} {msg.text}</span>}
            </form>
          )}

          <div className="gxw-opt is-featured">
            <span className="gxw-opt-flag">Best start</span>
            <span className="gxw-opt-icon"><Gift size={18} /></span>
            <strong>Starter <em>$9.99</em></strong>
            <span className="gxw-opt-note">{first ? 'Your first avatar plus 4 more builds and 6 recolors.' : '5 avatar or outfit builds and 6 recolors.'} One-time.</span>
            <button className="gxw-btn-primary" disabled={!paymentsEnabled} onClick={() => onBuy('starter')}>
              {paymentsEnabled ? 'Get Starter' : 'Coming soon'}
            </button>
          </div>

          <div className="gxw-opt">
            <span className="gxw-opt-icon is-gold"><Crown size={18} /></span>
            <strong>Creator package</strong>
            <span className="gxw-opt-note">Join the Creator Council: monthly AI, Studio and creator perks.</span>
            {paymentsEnabled
              ? <button className="gxw-btn-ghost" onClick={() => onBuy('council')}>See packages</button>
              : <a className="gxw-btn-ghost" href="/council" target="_blank" rel="noopener noreferrer">See packages</a>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Flow ----------
export interface AvatarCreateFlowProps {
  variant: 'first' | 'more';
  equip: 'always' | 'ask';
  /** Where a redirect-based payment comes back to (must be allowed by /api/checkout/session). */
  returnPath: string;
  /** First run: "Skip for now". */
  onSkip?: () => void;
  /** Later avatars: "Cancel" (back to wardrobe / world). */
  onCancel?: () => void;
  cancelLabel?: string;
  /** Leave the flow after the done screen. `equipped` says whether it is now worn. */
  onFinish: (avatar: Avatar | null, equipped: boolean) => void;
  finishLabel?: string;
  /** Rendered inside a modal (no full-page minimum height). */
  inModal?: boolean;
}

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'realistic', label: 'Realistic' },
  { id: 'stylized', label: 'Stylized' },
];

const AvatarCreateFlow: React.FC<AvatarCreateFlowProps> = ({
  variant, equip, returnPath, onSkip, onCancel, cancelLabel = 'Cancel', onFinish, finishLabel, inModal,
}) => {
  const [params, setParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('choose');
  const [vrmOpen, setVrmOpen] = useState(false);
  const [status, setStatus] = useState<OnboardingStatus | null>(null);

  // starter path
  const [filter, setFilter] = useState<Filter>('all');
  const [picked, setPicked] = useState<AvatarStyle | null>(null);
  const [selfie, setSelfie] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // build
  const [stage, setStage] = useState('');
  const [progress, setProgress] = useState(0);
  const [buildErr, setBuildErr] = useState('');
  const [buildArt, setBuildArt] = useState<string>('');
  const [built, setBuilt] = useState<Avatar | null>(null);
  const [equipped, setEquipped] = useState(false);
  const [wearing, setWearing] = useState(false);

  // free starter look
  const [templates, setTemplates] = useState<StarterTemplate[]>([]);
  const [freePicked, setFreePicked] = useState<StarterTemplate | null>(null);
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimErr, setClaimErr] = useState('');
  const [tsToken, setTsToken] = useState<string | null>(null);
  const [tsReset, setTsReset] = useState(0);
  const [email, setEmail] = useState('');
  const [resend, setResend] = useState<{ busy: boolean; msg: string }>({ busy: false, msg: '' });
  const [resendToken, setResendToken] = useState<string | null>(null);
  const [resendReset, setResendReset] = useState(0);

  // gate + checkout
  const [gateOpen, setGateOpen] = useState(false);
  const [checkout, setCheckout] = useState<ProductKey | 'council' | null>(null);
  const gateResolve = useRef<((ok: boolean) => void) | null>(null);

  const refreshStatus = useCallback(async () => {
    const s = await fetchOnboardingStatus();
    setStatus(s);
    return s;
  }, []);

  const finishBuild = useCallback(async (avatar: Avatar) => {
    if (equip === 'always') {
      if (!avatar.is_active) { await setActiveAvatar(avatar.id); avatar.is_active = true; }
      rememberAvatarLocally(avatar);
      setEquipped(true);
    }
    setBuilt(avatar);
    setPhase('done');
  }, [equip]);

  // Status, a payment coming back from a redirect, and a build that was still
  // running when the page was closed (the server kept the paid work).
  useEffect(() => {
    (async () => {
      const s0 = await refreshStatus();
      if (s0?.free_starter) void fetchStarterTemplates().then(setTemplates);
      void supabase.auth.getSession().then(({ data }) => setEmail(data.session?.user?.email || ''));
      const cs = params.get('checkout');
      if (cs) {
        await waitForPaid(cs);
        await refreshStatus();
        params.delete('checkout');
        setParams(params, { replace: true });
      }
      const pending = pendingAvatarBuild();
      if (pending) {
        setBuildArt(pending.image || '');
        setPhase('building'); setStage('Picking up your build…'); setProgress(0);
        try {
          await finishBuild(await finishAvatarBuild(pending.id, { progress: { onStage: setStage, onProgress: setProgress } }));
        } catch (err: any) {
          setBuildErr(err?.message || 'The build failed. Nothing was charged for a failed build — try again.');
          setPhase('choose');
        }
      }
    })().catch((err) => console.error('[avatar-create] start failed', err));
    // Run once on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Ask for the unlock if needed. Resolves true when the build may run. */
  const ensureUnlocked = useCallback(async (): Promise<boolean> => {
    const s = await refreshStatus();
    if (!s || s.can_build) return true; // unknown status: let the server decide
    setGateOpen(true);
    return new Promise<boolean>((resolve) => { gateResolve.current = resolve; });
  }, [refreshStatus]);

  const closeGate = (ok: boolean) => {
    setGateOpen(false);
    gateResolve.current?.(ok);
    gateResolve.current = null;
  };

  const onPaid = async () => {
    setCheckout(null);
    const s = await refreshStatus();
    if (s?.can_build) closeGate(true);
  };

  const cards = useMemo(() => AVATAR_STYLES.filter((s) => filter === 'all' || s.kind === filter), [filter]);

  const pick = (style: AvatarStyle) => { setPicked(style); setPreview(null); setBuildErr(''); };

  const onSelfie = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !f.type.startsWith('image/')) return;
    const r = new FileReader();
    r.onload = () => { setSelfie(r.result as string); setPreview(null); };
    r.readAsDataURL(f);
  };

  const makeLikeMe = async () => {
    if (!picked || !selfie) return;
    setPreviewBusy(true); setBuildErr('');
    try {
      const style = await styleImageDataUrl(picked.id);
      const img = await stylizeSelfie(selfie, style, picked.styleName);
      setPreview(img.dataUrl);
    } catch (err: any) {
      setBuildErr(err?.message || 'Could not restyle that photo.');
    } finally {
      setPreviewBusy(false);
    }
  };

  const buildFromStyle = async () => {
    if (!picked) return;
    if (!(await ensureUnlocked())) return;
    const art = preview || styleDetail(picked.id);
    setBuildArt(art);
    setPhase('building'); setBuildErr(''); setProgress(0); setStage('Preparing your look…');
    try {
      const image = preview || await styleImageDataUrl(picked.id);
      const avatar = await buildAvatarFromImage({
        imageDataUrl: image,
        name: picked.kind === 'realistic' ? `My avatar (${picked.name})` : `My ${picked.name} avatar`,
        creationParams: { onboarding_style: picked.id, from_selfie: preview ? 'yes' : 'no' },
        setActive: false,
        previewImage: await styleThumbDataUrl(art),
        progress: { onStage: setStage, onProgress: setProgress },
      });
      await finishBuild(avatar);
    } catch (err: any) {
      setBuildErr(err?.message || 'The build failed. Nothing was charged for a failed build — try again.');
      setPhase('choose');
      if (err instanceof AvatarBuildError && err.needsPlan) setGateOpen(true);
    }
  };

  // ---- Free starter look: an instant copy, equipped right away ----
  const showFree = !!status?.free_starter && templates.length > 0;
  const confirmed = status?.email_confirmed !== false;

  const pickFree = (t: StarterTemplate) => { setFreePicked(t); setPicked(null); setClaimErr(''); };

  const claimFree = async () => {
    if (!freePicked) return;
    setClaimBusy(true); setClaimErr('');
    const r = await claimStarterAvatar(freePicked.look_id, tsToken);
    setClaimBusy(false);
    if (r.ok && r.avatar) {
      rememberAvatarLocally(r.avatar);
      setEquipped(true);
      setBuilt(r.avatar);
      setPhase('done');
      void refreshStatus();
      return;
    }
    setClaimErr(r.message || 'Could not claim that avatar. Try again.');
    setTsReset((n) => n + 1); // Turnstile tokens are single-use
    if (r.reason === 'already_claimed' || r.reason === 'unconfirmed') {
      const s = await refreshStatus();
      if (!s?.free_starter) setFreePicked(null);
    }
  };

  const resendEmail = async () => {
    if (!email) return;
    setResend({ busy: true, msg: '' });
    const r = await resendConfirmation(email, resendToken);
    setResend({ busy: false, msg: r.message });
    setResendReset((n) => n + 1);
  };

  const wearNow = async () => {
    if (!built) return;
    setWearing(true);
    try {
      await setActiveAvatar(built.id);
      built.is_active = true;
      rememberAvatarLocally(built);
      onFinish(built, true);
    } catch (err: any) {
      setBuildErr(err?.message || 'Could not equip that avatar.');
    } finally {
      setWearing(false);
    }
  };

  const describeDone = (avatar: Avatar, wasEquipped: boolean) => {
    if (wasEquipped) rememberAvatarLocally(avatar);
    setBuilt(avatar);
    setEquipped(wasEquipped);
    void refreshStatus();
    setPhase('done');
  };

  const first = variant === 'first';
  // Custom looks need a code, Starter or a Creator package on gated accounts.
  const customLocked = !!status?.gated && !status.can_build;

  return (
    <div className={`gxw${inModal ? ' gxw-in-modal' : ''}`}>
      <header className="gxw-top">
        <span className="gx-lockup gxw-lockup" aria-label="GathrXR">
          <img src="/brand/gathrxr-mark.png" alt="" />
          <span className="gx-word">Gathr<span>XR</span></span>
        </span>
        {phase === 'choose' && onSkip && <button className="gxw-skip" onClick={onSkip}>Skip for now</button>}
        {phase !== 'building' && onCancel && (
          <button className="gxw-skip" onClick={onCancel}><ArrowLeft size={14} /> {cancelLabel}</button>
        )}
      </header>

      {phase === 'choose' && (
        <main className="gxw-main">
          <div className="gxw-intro">
            <span className="gxw-eyebrow"><Hand size={13} /> {first ? 'Welcome to GathrXR' : 'New avatar'}</span>
            <h1>{showFree ? 'Pick your avatar' : first ? 'Create your first avatar' : 'Create an avatar'}</h1>
            <p className="gxw-lede">
              {showFree
                ? 'Pick a free robot to start playing — or create your own avatar (Starter plan / code).'
                : 'Start from a look you love, or describe exactly who you want to be.'}
            </p>
            {buildErr && <p className="gxw-err" role="alert">{buildErr}</p>}
            <button className="gxw-vrm-link" onClick={() => setVrmOpen(true)}>
              <Box size={14} /> Already have a VRoid / VRM avatar? Upload it free
            </button>
          </div>
          {vrmOpen && (
            <React.Suspense fallback={null}>
              <VrmUploadDialog
                onClose={() => setVrmOpen(false)}
                onUploaded={(avatar, equipped) => {
                  if (equipped) rememberAvatarLocally(avatar);
                  onFinish(avatar, equipped);
                }}
              />
            </React.Suspense>
          )}

          <div className="gxw-split">
            {/* ---- Left: start from a look ---- */}
            <section className="gxw-panel" aria-label="Start from a look">
              <div className="gxw-panel-head">
                {picked || freePicked ? (
                  <button className="gxw-back" onClick={() => { setPicked(null); setPreview(null); setFreePicked(null); setClaimErr(''); }}>
                    <ArrowLeft size={15} /> All looks
                  </button>
                ) : <h2><Shirt size={16} /> Start from a look</h2>}
                <span className="gxw-panel-tag">{freePicked ? 'Free' : picked && customLocked ? 'Starter plan / code' : 'Fastest'}</span>
              </div>

              {!picked && !freePicked && showFree && (
                <div className="gxw-free" aria-label="Free robots">
                  <div className="gxw-free-head">
                    <strong><Sparkles size={15} /> Free robot{templates.length === 1 ? '' : 's'}</strong>
                    <span>Ready instantly · one per account</span>
                  </div>
                  {!confirmed && (
                    <div className="gxw-free-confirm" role="status">
                      <Mail size={15} />
                      <span>Confirm your email to unlock your free avatar.{email ? <> We sent a link to <b>{email}</b>.</> : null}</span>
                      <TurnstileWidget className="gxw-turnstile" action="resend" onToken={setResendToken} resetKey={resendReset} />
                      <button className="gxw-btn-ghost" onClick={resendEmail} disabled={resend.busy || !email || (turnstileEnabled && !resendToken)}>
                        {resend.busy ? <Loader size={14} className="spin" /> : 'Resend email'}
                      </button>
                      {resend.msg && <em>{resend.msg}</em>}
                    </div>
                  )}
                  <div className="gxw-grid gxw-free-grid">
                    {templates.map((t, i) => (
                      <button key={t.look_id} className="gxw-card gxw-card-free" onClick={() => pickFree(t)} disabled={!confirmed} style={{ animationDelay: `${i * 30}ms` }}>
                        {t.thumbnail_url ? <img src={t.thumbnail_url} alt="" /> : <span className="gxw-build-ph"><Shirt size={32} /></span>}
                        <span className="gxw-card-free-badge">Free</span>
                        <span className="gxw-card-label">
                          <strong>{t.name}</strong>
                          <em>{t.tagline || 'Ready now'}</em>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!picked && !freePicked && showFree && (
                <div className="gxw-custom-head">
                  <strong><Crown size={14} /> Create your own</strong>
                  <span>{customLocked ? 'Starter plan or code' : 'Built for you in a few minutes'}</span>
                </div>
              )}

              {freePicked && (
                <div className="gxw-picked">
                  <div className="gxw-picked-art gxw-picked-art-free">
                    {freePicked.thumbnail_url ? <img src={freePicked.thumbnail_url} alt={`${freePicked.name} avatar`} /> : <span className="gxw-build-ph"><Shirt size={40} /></span>}
                    <span className="gxw-picked-badge">Free</span>
                  </div>
                  <div className="gxw-picked-side">
                    <h3>{freePicked.name}</h3>
                    <p>Ready instantly — no build, no code. This exact avatar walks, runs and emotes in every world, and it's yours to keep.</p>
                    <TurnstileWidget className="gxw-turnstile" action="claim-starter" onToken={setTsToken} resetKey={tsReset} />
                    <button className="gxw-btn-primary gxw-build" onClick={claimFree} disabled={claimBusy || !confirmed || (turnstileEnabled && !tsToken)}>
                      {claimBusy ? <Loader size={16} className="spin" /> : <><Gift size={16} /> Get it free</>}
                    </button>
                    {claimErr && <p className="gxw-err" role="alert">{claimErr}</p>}
                    <span className="gxw-small">One free robot per account. Want a custom look? Pick any look under "Create your own" or describe one (Starter plan or code).</span>
                  </div>
                </div>
              )}

              {!picked && !freePicked && (
                <>
                  <div className="gxw-filters" role="tablist" aria-label="Filter looks">
                    {FILTERS.map((f) => (
                      <button key={f.id} role="tab" aria-selected={filter === f.id} className={`gxw-filter${filter === f.id ? ' is-on' : ''}`} onClick={() => setFilter(f.id)}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <div className="gxw-grid">
                    {cards.map((s, i) => (
                      <button key={s.id} className="gxw-card" onClick={() => pick(s)} style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
                        <img src={styleCard(s.id)} alt="" loading={i < 8 ? 'eager' : 'lazy'} />
                        <span className="gxw-card-label">
                          <strong>{s.name}</strong>
                          <em>{s.tagline}</em>
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {picked && (
                <div className="gxw-picked">
                  <div className="gxw-picked-art">
                    <img src={preview || styleDetail(picked.id)} alt={`${picked.name} avatar preview`} />
                    {previewBusy && <span className="gxw-picked-busy"><Loader size={22} className="spin" /> Restyling your photo…</span>}
                    {preview && <span className="gxw-picked-badge">Your version</span>}
                  </div>
                  <div className="gxw-picked-side">
                    <h3>{picked.name}</h3>
                    <p>We'll turn this into a rigged 3D avatar that walks, sits and emotes.</p>

                    <div className="gxw-likeme">
                      <strong><Camera size={15} /> Make it look like me <em>optional</em></strong>
                      <span>Add a clear, front-facing photo and we'll draw you in this style.</span>
                      <div className="gxw-likeme-row">
                        {selfie && <img src={selfie} alt="Your photo" className="gxw-selfie" />}
                        <button className="gxw-btn-ghost" onClick={() => fileRef.current?.click()}>{selfie ? 'Change photo' : 'Add a photo'}</button>
                        {selfie && (
                          <button className="gxw-btn-ghost" onClick={makeLikeMe} disabled={previewBusy}>
                            {preview ? <><RefreshCw size={14} /> Redo</> : <><Wand2 size={14} /> Preview me</>}
                          </button>
                        )}
                        {selfie && <button className="gxw-linkbtn" onClick={() => { setSelfie(null); setPreview(null); }}>Remove</button>}
                      </div>
                      <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onSelfie} hidden />
                    </div>

                    <button className="gxw-btn-primary gxw-build" onClick={buildFromStyle} disabled={previewBusy || (!!selfie && !preview)}>
                      <Box size={16} /> Build my avatar
                    </button>
                    {!!selfie && !preview && <span className="gxw-small">Preview your photo first, or remove it to use the look as shown.</span>}
                    {customLocked && <span className="gxw-small">Custom builds use the Starter plan or a code{showFree ? ' — or go back for a free robot.' : '.'}</span>}
                  </div>
                </div>
              )}
            </section>

            <div className="gxw-or" aria-hidden><span>or</span></div>

            {/* ---- Right: describe it (the creator wizard, inline) ---- */}
            <section className="gxw-panel gxw-panel-describe" aria-label="Describe it">
              <div className="gxw-panel-head">
                <h2><Wand2 size={16} /> Describe it</h2>
                <span className="gxw-panel-tag">{customLocked ? 'Starter plan / code' : 'Most control'}</span>
              </div>
              <div className="gxw-creator">
                <AvatarCreator
                  embedded
                  beforeBuild={ensureUnlocked}
                  onClose={() => undefined}
                  onAvatarCreated={() => undefined}
                  onEquipped={(a) => describeDone(a, true)}
                  onKept={equip === 'ask' ? (a) => describeDone(a, false) : undefined}
                />
              </div>
            </section>
          </div>
        </main>
      )}

      {phase === 'building' && (
        <main className="gxw-narrow gxw-building">
          <div className="gxw-build-art">
            {buildArt ? <img src={buildArt} alt="" /> : <span className="gxw-build-ph"><Shirt size={40} /></span>}
            <div className="gxw-scan" aria-hidden />
          </div>
          <h1>Building your avatar</h1>
          <p className="gxw-lede">{stage || 'Starting…'} This takes a few minutes — keep this open.</p>
          <div className="gxw-bar"><i style={{ width: `${progress}%` }} /></div>
          <span className="gxw-small">{progress}%</span>
        </main>
      )}

      {phase === 'done' && (
        <main className="gxw-narrow gxw-done">
          {built?.thumbnail_url
            ? <div className="gxw-done-art"><img src={built.thumbnail_url} alt="" /><span className="gxw-done-tick"><Check size={18} /></span></div>
            : <div className="gxw-done-check"><Check size={30} /></div>}
          <h1>{first ? "You're ready" : 'Your new avatar is ready'}</h1>
          <p className="gxw-lede">
            {equipped
              ? `${built?.name || 'Your avatar'} is equipped. Jump into a world and say hi.`
              : `${built?.name || 'Your avatar'} is in your wardrobe.`}
          </p>
          {buildErr && <p className="gxw-err" role="alert">{buildErr}</p>}
          <div className="gxw-done-actions">
            {equipped ? (
              <button className="gxw-btn-primary" onClick={() => onFinish(built, true)}>{finishLabel || "Let's go"} <ChevronRight size={16} /></button>
            ) : (
              <>
                <button className="gxw-btn-primary" onClick={wearNow} disabled={wearing}>
                  {wearing ? <Loader size={16} className="spin" /> : <><Shirt size={16} /> Wear it now</>}
                </button>
                <button className="gxw-btn-ghost gxw-btn-tall" onClick={() => onFinish(built, false)} disabled={wearing}>{finishLabel || 'Back to wardrobe'}</button>
              </>
            )}
          </div>
        </main>
      )}

      {gateOpen && <UnlockGate status={status} onUnlocked={async () => { await refreshStatus(); closeGate(true); }} onClose={() => closeGate(false)} onBuy={setCheckout} />}
      {checkout && <CheckoutModal product={checkout} returnPath={returnPath} onClose={() => setCheckout(null)} onPaid={onPaid} />}
    </div>
  );
};

export default AvatarCreateFlow;
